// "Yönetici ata" / "Rol ve kapsam" penceresi — portal › Yöneticiler (Süper Admin) ve danışman ›
// Müşteri yöneticileri kartı ORTAK (portal yetki v2 §5/§8.2). Kişi (yalnız yeni atamada), ROL
// (zorunlu, native radio — R2-15: danışman yolunda varsayılan firmada Süper Admin yoksa Süper Admin,
// varsa Admin), Admin'de kapsam (kategori onay kutuları = firma setlerindeki kategori birleşimi +
// işletme ağacı) ve "Kapsamıyla aynı set erişimini de ver" (varsayılan işaretli). Süper Admin'de
// kapsam gövdeye hiç yazılmaz (sunucu 422). API çağrısı üst bileşende (`onSubmit`).

import { useEffect, useId, useState } from "react";
import {
  CLIENT_ADMIN_ROLES,
  CLIENT_ADMIN_ROLE_LABELS,
  type ClientAdminRole,
  type ClientAdminRow,
  type ClientAdminSet,
  type ClientEntity,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import { isValidEmail } from "../../lib/email";
import { CLIENT_ADMIN_COPY, CLIENT_ADMINS_CARD_COPY as C } from "../../lib/client-admin-copy";
import {
  buildAdminScope,
  leafEntityIds,
  scopeCategoryOptions,
} from "../../lib/client-access-draft-v2";
import { EntityCheckTree } from "../clients/EntityTree";

export interface AdminFormValue {
  fullName: string;
  email: string;
  role: ClientAdminRole;
  /**
   * Yalnız `role === "admin"`; ikisi de null = tam kapsam. `undefined` = GÖNDERME (PATCH "yok = koru"):
   * kapsam listeleri yokken / değiştirilecek boyut yokken mevcut kapsam asla yeniden yazılmaz.
   */
  scope?: { categories: string[] | null; entities: string[] | null } | undefined;
  grantMatching: boolean;
}

interface Props {
  open: boolean;
  /** Verilirse düzenleme modu (kişi sabit; rol + kapsam). */
  row?: ClientAdminRow | undefined;
  sets: ClientAdminSet[];
  entities: ClientEntity[];
  /**
   * Kategori/işletme listeleri: `failed`/`loading` iken Admin kaydedilemez (kapsam bilinmeden yazılmaz);
   * `ready` + set yok = firmada henüz set yok (kategori kapsamı korunur, yalnız rol/işletme).
   */
  scopeStatus: "loading" | "ready" | "failed";
  onRetryScope?: (() => void) | undefined;
  /** Düzenleme: bu kişi son aktif Süper Admin ve Admin'e düşürülüyor → uyarı (danışman muaf). */
  warnLastSuper?: boolean | undefined;
  defaultRole: ClientAdminRole;
  onClose: () => void;
  onSubmit: (value: AdminFormValue) => Promise<void>;
}

export function AdminGrantModal(props: Props): JSX.Element | null {
  const { open, row, sets, entities, scopeStatus, defaultRole, onClose, onSubmit } = props;
  const edit = row !== undefined;
  const [fullName, setFullName] = useState(row?.fullName ?? "");
  const [email, setEmail] = useState(row?.email ?? "");
  const [role, setRole] = useState<ClientAdminRole>(row?.role ?? defaultRole);
  const [allCats, setAllCats] = useState(row?.scope?.categories == null);
  const [cats, setCats] = useState<Set<string>>(new Set(row?.scope?.categories ?? []));
  const [allEnts, setAllEnts] = useState(row?.scope?.entities == null);
  const [ents, setEnts] = useState<Set<string>>(new Set(row?.scope?.entities ?? []));
  const [grantMatching, setGrantMatching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const titleId = useId();
  const catOptions = scopeCategoryOptions(sets);
  const leafIds = leafEntityIds(entities);
  const scopeReady = scopeStatus === "ready";
  const categoriesEditable = scopeReady && sets.length > 0;
  const entitiesEditable = scopeReady && leafIds.length > 1;

  // Açılışta (yeni satır / varsayılan rol) sıfırla
  useEffect(() => {
    if (!open) return;
    setFullName(row?.fullName ?? "");
    setEmail(row?.email ?? "");
    setRole(row?.role ?? defaultRole);
    setAllCats(row?.scope?.categories == null);
    setCats(new Set(row?.scope?.categories ?? []));
    setAllEnts(row?.scope?.entities == null);
    setEnts(new Set(row?.scope?.entities ?? []));
    setGrantMatching(true);
    setError(null);
    setTouched(false);
  }, [open, row, defaultRole]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape" && !loading) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const emailOk = edit || isValidEmail(email);
  const scopeInvalid =
    role === "admin" &&
    ((categoriesEditable && !allCats && cats.size === 0) ||
      (entitiesEditable && !allEnts && ents.size === 0));
  // Admin + kapsam listeleri yok → kapsam bilinmeden kaydedilmez (sessiz tam kapsam yok).
  const scopeBlocked = role === "admin" && !scopeReady;
  const canSubmit =
    !loading &&
    !scopeInvalid &&
    !scopeBlocked &&
    (edit || (email.trim().length > 3 && fullName.trim().length > 1));

  async function handleSubmit(): Promise<void> {
    setTouched(true);
    if (!emailOk) {
      setError(C.modal.emailInvalid);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        fullName: fullName.trim(),
        email: email.trim(),
        role,
        scope: buildAdminScope({
          role,
          edit,
          categoriesEditable,
          entitiesEditable,
          allCats,
          cats,
          allEnts,
          ents,
          current: row?.scope,
        }),
        grantMatching,
      });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === "CLIENT_ADMIN_LAST"
            ? CLIENT_ADMIN_COPY.admins.lastSuper
            : (C.modal.errors[err.code] ?? C.modal.genericError)
          : C.modal.genericError,
      );
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    borderColor: "var(--color-border)",
    fontFamily: "var(--font-sans)",
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={() => !loading && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="rounded-xl p-6 max-w-lg w-full mx-4 flex flex-col gap-3"
        style={{
          background: "var(--color-surface)",
          color: "var(--color-text)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} style={{ fontFamily: "var(--font-display)", fontSize: "1.25rem" }}>
          {edit ? C.modal.editTitle : C.modal.title}
        </h2>
        {edit ? (
          <p className="text-sm" style={{ color: "var(--color-text-2)" }}>
            <strong>{row.fullName}</strong> · {row.email}
          </p>
        ) : (
          <>
            <p className="text-sm" style={{ color: "var(--color-text-2)" }}>
              {C.modal.description}
            </p>
            <label className="block text-xs" style={{ color: "var(--color-text-2)" }}>
              {C.modal.fullName}
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
                className="w-full p-2 rounded border text-sm mt-1"
                style={inputStyle}
              />
            </label>
            <label className="block text-xs" style={{ color: "var(--color-text-2)" }}>
              {C.modal.email}
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error === C.modal.emailInvalid) setError(null);
                }}
                onBlur={() => setTouched(true)}
                disabled={loading}
                aria-invalid={touched && !emailOk && email.trim() !== "" ? true : undefined}
                className="w-full p-2 rounded border text-sm mt-1"
                style={{
                  ...inputStyle,
                  ...(touched && !emailOk && email.trim() !== ""
                    ? { borderColor: "var(--color-error)" }
                    : {}),
                }}
              />
              {touched && !emailOk && email.trim() !== "" ? (
                <span className="block mt-1" style={{ color: "var(--color-error)" }}>
                  {C.modal.emailInvalid}
                </span>
              ) : null}
            </label>
          </>
        )}

        {/* Rol — zorunlu, tek seçim (native radio) */}
        <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
          <legend
            className="text-sm mb-1"
            style={{ color: "var(--color-text-2)", fontWeight: 600 }}
          >
            {C.modal.role}
          </legend>
          {CLIENT_ADMIN_ROLES.map((r) => (
            <label key={r} className="flex items-start gap-2 text-sm py-0.5">
              <input
                type="radio"
                name="admin-role"
                value={r}
                checked={role === r}
                disabled={loading}
                onChange={() => setRole(r)}
                className="mt-1"
              />
              <span>
                {CLIENT_ADMIN_ROLE_LABELS[r]}
                <span className="block text-xs" style={{ color: "var(--color-text-3)" }}>
                  {C.modal.roleHelp[r]}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        {/* Admin kapsamı */}
        {role === "admin" ? (
          <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
            <legend
              className="text-sm mb-1"
              style={{ color: "var(--color-text-2)", fontWeight: 600 }}
            >
              {C.modal.scope}
            </legend>
            {!scopeReady ? (
              <div
                className="text-xs flex items-center gap-2"
                style={{ color: "var(--color-text-3)" }}
              >
                {scopeStatus === "loading" ? CLIENT_ADMIN_COPY.loading : C.modal.scopeLoadFailed}
                {scopeStatus === "failed" && props.onRetryScope ? (
                  <button
                    type="button"
                    onClick={props.onRetryScope}
                    className="underline"
                    style={{ color: "var(--color-primary)", fontWeight: 600 }}
                  >
                    {C.modal.scopeRetry}
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {!categoriesEditable ? (
                  <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
                    {C.modal.scopeNoSets}
                  </p>
                ) : (
                  <div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={allCats}
                        disabled={loading}
                        onChange={(e) => setAllCats(e.target.checked)}
                      />
                      {C.modal.allCategories}
                    </label>
                    {!allCats ? (
                      <div className="mt-1 pl-6 flex flex-col">
                        {catOptions.map((c) => (
                          <label
                            key={c.metricId}
                            className="flex items-center gap-2 text-sm py-0.5"
                          >
                            <input
                              type="checkbox"
                              checked={cats.has(c.metricId)}
                              disabled={loading}
                              onChange={(e) =>
                                setCats((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(c.metricId);
                                  else next.delete(c.metricId);
                                  return next;
                                })
                              }
                            />
                            {c.label}
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
                {leafIds.length > 1 ? (
                  <div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={allEnts}
                        disabled={loading}
                        onChange={(e) => {
                          setAllEnts(e.target.checked);
                          if (!e.target.checked && ents.size === 0) setEnts(new Set(leafIds));
                        }}
                      />
                      {C.modal.allEntities}
                    </label>
                    {!allEnts ? (
                      <div className="mt-1 pl-6">
                        <EntityCheckTree
                          entities={entities}
                          checked={ents}
                          disabled={loading}
                          ariaLabel={CLIENT_ADMIN_COPY.drawer.entities.treeAria}
                          inactiveLabel={CLIENT_ADMIN_COPY.drawer.entities.inactive}
                          onToggle={(ids, on) =>
                            setEnts((prev) => {
                              const next = new Set(prev);
                              for (const id of ids) {
                                if (on) next.add(id);
                                else next.delete(id);
                              }
                              return next;
                            })
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {scopeInvalid ? (
                  <p role="alert" className="text-xs" style={{ color: "var(--color-error)" }}>
                    {C.modal.scopeEmpty}
                  </p>
                ) : null}
              </div>
            )}
          </fieldset>
        ) : null}

        {edit && props.warnLastSuper && role === "admin" && row.role === "super_admin" ? (
          <p
            role="note"
            className="rounded-md px-3 py-2 text-xs"
            style={{
              background: "var(--color-warning-bg)",
              color: "var(--color-warning-text)",
              border: "1px solid var(--color-warning-border)",
            }}
          >
            {C.noSuperWarn}
          </p>
        ) : null}

        {!edit ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={grantMatching}
              disabled={loading}
              onChange={(e) => setGrantMatching(e.target.checked)}
            />
            {CLIENT_ADMIN_COPY.admins.grantMatching}
          </label>
        ) : null}

        {error ? (
          <div
            role="alert"
            className="rounded-md p-3 text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={() => !loading && onClose()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm"
            style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
          >
            {C.modal.cancel}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {loading
              ? edit
                ? C.modal.saving
                : C.modal.submitting
              : edit
                ? C.modal.save
                : C.modal.submit}
          </button>
        </div>
      </div>
    </div>
  );
}
